import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    ChevronLeft, ChevronRight, Plus, Calendar, Clock, DoorOpen, Columns3,
} from 'lucide-react';
import api from '../services/api';
import BarraAgende from '../components/agenda/BarraAgendeConsole';
import { usePagineDiColonne, NavigazionePagine } from '../components/agenda/PagineDiColonne';
import AgendaSettimana, { lunediDi, piuGiorni } from '../components/agenda/AgendaSettimanaConsole';
import { PannelloAppuntamento, PannelloNuovo } from '../components/agenda/PannelliAgenda';
import { giornoValido } from '../components/agenda/giorno';
import BloccoAppuntamento from '../components/agenda/BloccoAppuntamento';
import useChiudiConEsc from '../components/agenda/useChiudiConEsc';
import {
    ORE, ORA_INIZIO, ORA_FINE, ALTEZZA_ORA, dueCifre, pixelDi, pixelDiOrario, disponi, riquadroDi,
    oraCoperta, useAdesso,
} from '../components/agenda/griglia';
import '../styles/AgendaConsole.css';
import '../styles/AgendaBarraConsole.css';
import { mescola } from '../utils/colori';
import { useAuthStore } from '../store/useStore';
import { puoOrganizzare } from '../utils/permessi';
import { ascolta, annunciaAgendaCambiata } from '../utils/annunci';
import useAzione from '../navigazione/useAzione';
import { notificaErrore, notificaAvviso, messaggioDa } from '../utils/notifiche';
import {
    oggiClinica, oraIta, oraDecimaleIta, giornoIta, momentoItaliano,
} from '../utils/oraItaliana';

/**
 * Agenda — la griglia giornaliera.
 *
 * Le colonne sono le agende che lavorano quel giorno: con quaranta
 * medici e dodici in sede, mostrarle tutte sarebbe illeggibile.
 *
 * Le sovrapposizioni si vedono affiancate, non nascoste: capita di
 * avere due pazienti insieme, e chi guarda deve accorgersene.
 *
 * LO STILE «CONSOLE» (mockup «Agenda», Alex 6/10/2026; docs/proposte/
 * 26-stile-console.md). Il dettaglio di un appuntamento e il nuovo
 * appuntamento non sono piu' finestre sopra la pagina ma un PANNELLO A
 * DESTRA: la griglia resta a video e si continua a guardarla, e un clic su
 * un altro appuntamento cambia il pannello invece di chiudere e riaprire
 * una finestra. La testata e' «‹ Oggi ›», il giorno, Giorno/Settimana, le
 * agende, «Appuntamento». Le regole sono tutte quelle delle rotte di prima
 * (abilitazione, sovrapposizioni, consensi, giorno della clinica, sala
 * d'attesa, legame alla visita aperta): qui cambia dove si vedono, non che
 * cosa decidono.
 */

/* «Oggi», le ore e le posizioni sulla griglia vengono tutte da
   `utils/oraItaliana.js` e da components/agenda/griglia.js. Prima erano
   `toISOString()` (che da' UTC) e `getHours()` (che da' il fuso del
   browser): nella stessa schermata convivevano due fusi, e nessuno dei due
   era quello della clinica. */
const oggiIso = () => oggiClinica();

/* Un giorno scritto nell'indirizzo vale solo se e' una data vera: la regola
   sta in components/agenda/giorno.js (la legge anche il pannello del nuovo
   appuntamento). */

const MAIUSCOLA = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** «Venerdì 2 ottobre», con l'anno solo se non e' quello di oggi. */
const titoloGiorno = (iso) => {
    const conAnno = iso.slice(0, 4) !== oggiIso().slice(0, 4);
    return MAIUSCOLA(new Date(`${iso}T12:00:00Z`).toLocaleDateString('it-IT', {
        timeZone: 'Europe/Rome', weekday: 'long', day: 'numeric', month: 'long',
        ...(conAnno ? { year: 'numeric' } : {}),
    }));
};

/** «6 – 12 ottobre», la settimana a video (col mese e l'anno quando servono). */
const titoloSettimana = (lunedi) => {
    const fine = piuGiorni(lunedi, 6);
    const conAnno = fine.slice(0, 4) !== oggiIso().slice(0, 4);
    const f = (iso, opz) => new Date(`${iso}T12:00:00Z`).toLocaleDateString('it-IT', { timeZone: 'Europe/Rome', ...opz });
    const stessoMese = lunedi.slice(0, 7) === fine.slice(0, 7);
    const da = stessoMese ? f(lunedi, { day: 'numeric' }) : f(lunedi, { day: 'numeric', month: 'long' });
    return `${da} – ${f(fine, { day: 'numeric', month: 'long', ...(conAnno ? { year: 'numeric' } : {}) })}`;
};

const AgendaConsole = () => {
    /* Le rotte di orari e stanze sono di admin e segreteria: il
       collegamento segue il permesso della rotta, non il contrario, e il
       permesso ha un nome solo, in utils/permessi.js. Fino al 22 settembre
       2026 questo nome era dichiarato nel componente del dettaglio e usato
       qui: ReferenceError in render, agenda giu' per tre giorni. */
    const ruolo = useAuthStore((st) => st.user?.ruolo);
    const organizza = puoOrganizzare(ruolo);
    /* Stesso commit, stesso difetto: `navigate` esisteva solo nel
       dettaglio, e i due pulsanti qui sopra sarebbero crollati al clic. */
    const navigate = useNavigate();
    /* IL GIORNO STA NELL'INDIRIZZO (Alex, 2/10/2026): ?giorno=AAAA-MM-GG,
       come le linguette del paziente dal 28/09. Prima era solo uno stato del
       componente, e un aggiornamento della pagina - anche quello che fa da
       sola quando esce una versione nuova (utils/versioneInLinea.js) -
       riportava l'agenda a oggi mentre si guardava un altro giorno.
       Letto all'apertura, scritto a ogni cambio di giorno SOSTITUENDO
       l'indirizzo (replace), non aggiungendo un passo: dieci clic sulla
       freccia non devono chiedere dieci «Indietro» per uscire dall'agenda.
       Oggi non si scrive: l'indirizzo senza giorno vuol dire «oggi», cosi'
       un'agenda lasciata su oggi e ricaricata domattina apre il giorno
       nuovo, non quello di ieri. Il giorno e' una data, non un dato di un
       paziente. La vista settimanale mostra la settimana di questo giorno. */
    const [parametri, setParametri] = useSearchParams();
    const giornoIndirizzo = parametri.get('giorno');
    const [data, setDataStato] = useState(() => giornoValido(giornoIndirizzo) || oggiIso());
    /* La casella della data, scritta a mano, passa da valori a meta' (un
       segmento cancellato, un anno di una cifra): l'agenda li prende come
       prima, e l'indirizzo segue solo un giorno vero. */
    const setData = (g) => {
        setDataStato(g);
        if (!giornoValido(g)) return;
        const scrivi = g === oggiIso() ? null : g;
        if (scrivi === giornoIndirizzo) return;
        setParametri((p) => {
            const n = new URLSearchParams(p);
            if (scrivi) n.set('giorno', scrivi); else n.delete('giorno');
            return n;
        }, { replace: true });
    };
    /* L'indirizzo cambiato da fuori: la voce «Agenda» del menu (apre oggi),
       «Indietro» e «Avanti» del browser (il giorno di quel passo). Un giorno
       che non esiste si toglie dall'indirizzo e si apre oggi. Il giorno
       appena scritto da qui coincide gia' con quello a video: niente da fare. */
    useEffect(() => {
        const valido = giornoValido(giornoIndirizzo);
        if (giornoIndirizzo !== null && !valido) {
            setParametri((p) => {
                const n = new URLSearchParams(p);
                n.delete('giorno');
                return n;
            }, { replace: true });
        }
        const atteso = valido || oggiIso();
        setDataStato((d) => (d === atteso ? d : atteso));
    }, [giornoIndirizzo]);
    const [griglia, setGriglia] = useState(null);
    const [caricamento, setCaricamento] = useState(true);
    const [errore, setErrore] = useState(null);

    /* IL PANNELLO A DESTRA (stile «Console», 6/10/2026). Uno solo alla volta:
       { tipo: 'appuntamento', id, iniziale } per il dettaglio, oppure
       { tipo: 'nuovo', contesto } per il nuovo appuntamento, dove contesto e'
       { agenda_id, agenda_nome, inizio, chiave } da un clic sulla griglia o
       { libero: true, giorno, chiave } dalla barra comandi, da «Nuovo» o da
       «Appuntamento». Prima erano due finestre sopra la pagina, e per
       guardare un altro appuntamento bisognava chiudere quella aperta. */
    const [pannello, setPannello] = useState(null);
    /* Il pannello aperto dice qui come chiedere prima di perdere quello che
       vi si e' scritto (useUscitaProtetta): un clic su un altro appuntamento
       o su un orario cambia pannello solo se la risposta e' si'. */
    const chiediPannello = useRef(null);
    const puoCambiarePannello = () => !chiediPannello.current || chiediPannello.current();
    /* Sale quando l'agenda si rilegge per un gesto fatto qui o annunciato da
       un'altra scheda: il pannello e la settimana si rileggono con lei. */
    const [versione, setVersione] = useState(0);

    /* «Nuovo appuntamento» dalla barra comandi (Ctrl K), da «Nuovo» o da
       «Appuntamento» qui in testata APRE IL PANNELLO (Alex, 4/10/2026:
       «"Nuovo appuntamento" da barra/Ctrl K apre il pannello»). Senza un
       clic sulla griglia, l'agenda, il giorno e l'ora si scelgono nel
       pannello: l'agenda e il giorno sono PROPOSTI (quella del medico
       collegato, altrimenti quella in vista; il giorno a video) e si vedono
       e si cambiano li', l'ora resta da scegliere - un orario inventato
       sarebbe un appuntamento preso dove nessuno ha scelto. Le regole del
       salvataggio sono quelle della rotta, come col clic. La chiave cambia a
       ogni apertura: la stessa azione con il pannello gia' aperto lo riapre
       pulito (la domanda sul lavoro non salvato la fa apriDestinazione,
       prima, o qui puoCambiarePannello). */
    const aperture = useRef(0);
    /* «Appuntamento» dalla cartella di un paziente (stile «Console»,
       6/10/2026) e' la stessa azione, col paziente gia' scelto: arriva nello
       stato della navigazione accanto all'azione, e useAzione lo consuma con
       lei e lo passa qui (`dati`): nella cronologia non resta (revisione del
       6/10/2026). Il paziente lo porta la cartella con i campi che il
       pannello mostra - quelli che darebbe la ricerca, mancate comprese - e
       il pannello si comporta come dopo averlo scelto: lo si toglie con la X
       e si cerca un altro. Nel pannello a destra dell'Agenda (unione dei due
       rami, 6/10/2026) viaggia nel contesto del nuovo appuntamento, accanto
       al giorno e alla chiave: NuovoAppuntamento lo legge da li'. */
    const apriNuovoLibero = (chiedere = true, paziente = null) => {
        if (chiedere && !puoCambiarePannello()) return;
        aperture.current += 1;
        setPannello({ tipo: 'nuovo', contesto: { libero: true, giorno: giornoValido(data) || oggiIso(), chiave: aperture.current,
                                                 paziente } });
    };
    useAzione({ 'nuovo-appuntamento': (azione, dati) => apriNuovoLibero(false, dati?.paziente || null) });
    const [tutteAgende, setTutteAgende] = useState([]);
    const [agendeLette, setAgendeLette] = useState(false);
    /* Il medico collegato all'utente: la sua agenda e' la proposta del
       pannello aperto senza un clic sulla griglia. */
    const medicoCollegato = useAuthStore((st) => st.user?.medico_id) || null;
    const [extra, setExtra] = useState([]);          // agende aggiunte a mano
    const [nascoste, setNascoste] = useState([]);    // colonne tolte dalla vista
    const [agendeAperte, setAgendeAperte] = useState(false);
    const sceltaAgende = useRef(null);
    useEffect(() => {
        if (!agendeAperte) return undefined;
        const fuori = (e) => {
            if (sceltaAgende.current && !sceltaAgende.current.contains(e.target)) setAgendeAperte(false);
        };
        document.addEventListener('mousedown', fuori);
        return () => document.removeEventListener('mousedown', fuori);
    }, [agendeAperte]);
    /* Esc chiude «Agende» anche col fuoco sul pulsante che l'ha aperto (il
       caso del mouse: prima solo col fuoco dentro il riquadro). Se il fuoco
       era dentro, torna sul pulsante: il riquadro chiuso e' nascosto, e chi
       usa la tastiera non resta nel vuoto. */
    useChiudiConEsc(agendeAperte, sceltaAgende, () => {
        const dentro = document.activeElement?.closest?.('#ag-barra-agende');
        setAgendeAperte(false);
        if (dentro) sceltaAgende.current?.querySelector('.ag-agende')?.focus();
    });
    const [vista, setVista] = useState('giorno');    // giorno | settimana
    const [agendaSett, setAgendaSett] = useState(null);
    const adesso = useAdesso();

    const corpo = useRef(null);
    const colonne = useRef({});   // id agenda -> elemento, per sapere dove si rilascia

    const carica = useCallback(async () => {
        try {
            setCaricamento(true);
            setErrore(null);
            const r = await api.get('/agenda/giorno', {
                params: { data, agende: extra.join(',') || undefined },
            });
            setGriglia(r.data.data);
        } catch (e) {
            setErrore(e?.response?.data?.message || 'Impossibile caricare l\'agenda');
        } finally {
            setCaricamento(false);
        }
    }, [data, extra]);

    useEffect(() => { carica(); }, [carica]);

    /* Rilegge la griglia, e con lei il pannello e la settimana. */
    const ricarica = useCallback(() => {
        carica();
        setVersione((v) => v + 1);
    }, [carica]);

    /* La griglia si rilegge quando qualcun altro cambia l'agenda.
       Oggi succede da un punto solo - il riquadro dei ritardi, che segna
       un paziente presente - ed e' esattamente il caso in cui il pallino
       giallo doveva comparire e non compariva: chi premeva «e' in sala
       d'attesa» vedeva sparire l'avviso e non succedere niente dove si
       aspettava di vederlo, cioe' sul nome in griglia. */
    useEffect(() => ascolta('agenda', ricarica), [ricarica]);

    /* L'elenco completo serve subito: la scelta delle agende lo mostra
       tutto, non solo chi lavora oggi. */
    useEffect(() => {
        api.get('/agenda/agende')
            .then((r) => setTutteAgende(r.data.data.agende || []))
            .catch(() => setTutteAgende([]))
            .finally(() => setAgendeLette(true));
    }, []);

    /* All'apertura la griglia si posiziona sull'ora corrente, non
       alle sette del mattino. Una volta per giorno: la griglia si rilegge a
       ogni gesto del pannello («Arrivato», «Sposta»), e prima a ogni
       rilettura tornava sull'ora di adesso, anche se si stava guardando la
       sera. */
    const posizionata = useRef(null);
    useEffect(() => {
        if (!griglia || !corpo.current || posizionata.current === griglia.data) return;
        posizionata.current = griglia.data;
        const ora = Number(momentoItaliano().ora.slice(0, 2));
        if (griglia.data === oggiIso() && ora > ORA_INIZIO + 2) {
            corpo.current.scrollTop = (ora - ORA_INIZIO - 1) * ALTEZZA_ORA;
        }
    }, [griglia]);

    /* L'APPUNTAMENTO APPENA PRESO DAL PANNELLO SI VEDE. Senza il clic sulla
       griglia il giorno e l'agenda del pannello possono non essere quelli a
       video: chi ha premuto «Prenota» deve ritrovarlo, non chiedersi se e'
       andata. Quindi l'agenda va su quel giorno (nell'indirizzo, come le
       frecce), nella vista del giorno, con la colonna di quell'agenda - se
       quel giorno non lavora, la colonna si aggiunge come dalla scelta delle
       agende - e la griglia scorre sull'ora. Dopo un clic sulla griglia non
       serve: giorno e colonna sono gia' quelli. */
    const daMostrare = useRef(null);   // { agendaId, giorno, ora, aggiunta }
    const mostraNuovo = (dove) => {
        daMostrare.current = { ...dove, aggiunta: false };
        setVista('giorno');
        setNascoste((n) => n.filter((x) => x !== dove.agendaId));
        if (dove.giorno === data) carica(); else setData(dove.giorno);
    };
    useEffect(() => {
        const m = daMostrare.current;
        if (!m || !griglia || griglia.data !== m.giorno) return;
        const inGriglia = (griglia.agende || []).some((a) => Number(a.id) === m.agendaId);
        if (!inGriglia && !m.aggiunta) {
            /* La griglia si rilegge con la colonna e si torna qui. Una volta
               sola: un'agenda chiusa per un'eccezione quel giorno il server
               non la mostra nemmeno scelta a mano, e non si gira a vuoto. */
            m.aggiunta = true;
            setExtra((e) => (e.includes(m.agendaId) ? e : [...e, m.agendaId]));
            return;
        }
        daMostrare.current = null;
        daPortare.current = m.agendaId;
        if (corpo.current) corpo.current.scrollTop = Math.max(0, (m.ora - ORA_INIZIO - 1) * ALTEZZA_ORA);
    }, [griglia]);

    /* Un passo delle frecce: un giorno, o una settimana nella vista
       settimanale. `new Date('AAAA-MM-GG')` e' mezzanotte UTC: sommare i
       giorni li' e rileggere con toISOString torna giusto, ma solo per
       caso. Si passa dal giorno italiano, come tutto il resto. */
    const passo = vista === 'settimana' ? 7 : 1;
    const cambiaGiorno = (giorni) => {
        const d = new Date(`${data}T12:00:00Z`);
        d.setUTCDate(d.getUTCDate() + giorni);
        setData(giornoIta(d));
    };

    /* --- linea dell'ora corrente: si muove con l'orologio (useAdesso) --- */
    const lineaAdesso = useMemo(() => {
        if (data !== oggiClinica(adesso)) return null;
        const h = oraDecimaleIta(adesso);
        if (h < ORA_INIZIO || h > ORA_FINE + 1) return null;
        return (h - ORA_INIZIO) * ALTEZZA_ORA;
    }, [data, adesso]);

    const fascePer = (agendaId) =>
        (griglia?.fasce || []).filter((f) => f.agenda_id === agendaId);

    const appPer = (agendaId) =>
        disponi((griglia?.appuntamenti || []).filter((a) => a.agenda_id === agendaId));

    /* Il colore della colonna e' quello dell'agenda, nei dati (Alex, 2/10:
       nessuna famiglia di colori nuova). Senza colore la colonna resta sui
       token del tema: niente grigio scritto a mano. */
    const coloreDi = (agendaId) => {
        const ag = (griglia?.agende || []).find((a) => a.id === agendaId)
            || tutteAgende.find((a) => a.id === agendaId);
        return ag?.colore || null;
    };

    /* «N appuntamenti» in testa alla colonna, come nel mockup: quelli coi
       pazienti, non i blocchi e non gli annullati. */
    const contoDi = (agendaId) => (griglia?.appuntamenti || []).filter((a) => a.agenda_id === agendaId
        && a.tipo === 'visita' && a.stato !== 'annullato').length;

    /* --- aprire il pannello --- */
    /* Aperto da tastiera (Invio o spazio sul blocco: un clic con
       detail 0) il fuoco va nel pannello, cosi' chi non usa il mouse
       arriva dove si e' aperto qualcosa; col mouse resta sulla griglia. */
    const apriAppuntamento = (x, e) => {
        if (pannello?.tipo === 'appuntamento' && pannello.id === x.id) return;
        if (!puoCambiarePannello()) return;
        setPannello({ tipo: 'appuntamento', id: x.id, iniziale: x, fuoco: !!e && e.detail === 0 });
    };
    const nuovoDaGriglia = (a, h, m) => {
        if (!puoCambiarePannello()) return;
        aperture.current += 1;
        setPannello({ tipo: 'nuovo', contesto: {
            agenda_id: a.id, agenda_nome: a.nome, inizio: `${data}T${dueCifre(h)}:${dueCifre(m)}`, chiave: aperture.current,
        } });
    };

    /* --- trascinamento degli appuntamenti ---
       Si sposta in verticale per cambiare ora, e di colonna per cambiare
       agenda (lo chiede, perche' vuol dire cambiare medico). Sotto i cinque
       pixel non e' un trascinamento ma un clic: il clic lo gestisce onClick,
       che c'e' anche da tastiera (Invio, spazio). Prima il dettaglio si
       apriva solo al rilascio del puntatore, e da tastiera non si apriva. */
    const [trascina, setTrascina] = useState(null);
    const appenaTrascinato = useRef(false);

    const iniziaTrascinamento = (e, app) => {
        if (app.stato === 'annullato' || e.button > 0) return;
        e.currentTarget.setPointerCapture(e.pointerId);
        setTrascina({ id: app.id, y0: e.clientY, dy: 0, x0: e.clientX, dx: 0 });
    };

    const muoviTrascinamento = (e) => {
        setTrascina((t) => (t
            ? { ...t, dy: e.clientY - t.y0, dx: e.clientX - t.x0 }
            : null));
    };

    const fineTrascinamento = async (e, app) => {
        if (!trascina || trascina.id !== app.id) return;
        const { dy, dx } = trascina;
        const cx = e.clientX;
        setTrascina(null);

        if (Math.abs(dy) < 5 && Math.abs(dx) < 5) return;   // un clic: ci pensa onClick
        appenaTrascinato.current = true;

        // un'ora vale ALTEZZA_ORA pixel; si arrotonda al quarto d'ora
        const minuti = Math.round(dy / ALTEZZA_ORA * 60 / 15) * 15;

        /* Su quale colonna e' stato lasciato: si misurano le colonne
           vere invece di calcolarne la larghezza, cosi' funziona
           anche quando cambia il numero di agende in griglia. */
        const sotto = Object.entries(colonne.current).find(([, el]) => {
            if (!el) return false;
            const r = el.getBoundingClientRect();
            return cx >= r.left && cx <= r.right;
        });
        const agendaDest = sotto ? parseInt(sotto[0], 10) : app.agenda_id;

        if (!minuti && agendaDest === app.agenda_id) return;

        /* Cambiare colonna vuol dire cambiare medico: un errore qui
           non si nota, l'appuntamento resta in griglia solo sotto
           il nome sbagliato. Quindi si chiede. */
        if (agendaDest !== app.agenda_id) {
            const nomeDi = (id) => (griglia?.agende || [])
                .find((z) => z.id === id)?.nome || 'un’altra agenda';
            const ok = window.confirm(
                `Spostare l’appuntamento da ${nomeDi(app.agenda_id)} `
                + `a ${nomeDi(agendaDest)}?`
            );
            if (!ok) { carica(); return; }
        }

        const vecchioInizio = new Date(app.inizio);
        const durata = new Date(app.fine) - vecchioInizio;
        const nuovoInizio = new Date(vecchioInizio.getTime() + minuti * 60000);

        try {
            const r = await api.put(`/agenda/${app.id}`, {
                inizio: nuovoInizio.toISOString(),
                fine: new Date(nuovoInizio.getTime() + durata).toISOString(),
                agenda_id: agendaDest,
            });
            const c = r.data.data?.conflitti;
            if (c && (c.stanza?.length || c.agenda?.length)) {
                notificaAvviso(r.data.message);
            }
        } catch (err) {
            notificaErrore(messaggioDa(err, 'Non riesco a spostare l’appuntamento'));
        }
        ricarica();
    };

    const clicSullAppuntamento = (x, e) => {
        if (appenaTrascinato.current) { appenaTrascinato.current = false; return; }
        apriAppuntamento(x, e);
    };

    /* Chi lavora oggi arriva dal server; qui si toglie solo chi
       l'utente ha deselezionato nella scelta delle agende. */
    const idsInGriglia = (griglia?.agende || []).map((a) => a.id);
    const agendeVisibili = (griglia?.agende || [])
        .filter((a) => !nascoste.includes(a.id));

    /* LE COLONNE A VIDEO sono una pagina delle agende scelte: tante quante
       ne entrano a 104 px, le altre dietro le frecce (Alex, 22/10/2026,
       «Pagine di colonne»: mai scorrimento di lato, a nessuna larghezza).
       Quali agende ci sono - chi lavora, le scelte con «Agende» - non cambia:
       cambia solo quante se ne vedono insieme. */
    const pagine = usePagineDiColonne(agendeVisibili.length);
    const colonneInVista = agendeVisibili.slice(pagine.inizio, pagine.fine);
    const portaInVista = pagine.porta;
    const misuraCorpo = pagine.osserva;
    const agganciaCorpo = useCallback((el) => {
        corpo.current = el;
        misuraCorpo(el);
    }, [misuraCorpo]);
    /* Un'agenda da portare in vista appena c'e': quella appena aggiunta da
       «Agende», quella dell'appuntamento appena preso, e - all'apertura - la
       propria di un medico (altrimenti con molte agende la prima pagina
       potrebbe non averla). Si cerca per id a ogni rilettura della griglia e
       si lascia quando la si e' trovata. */
    const daPortare = useRef(null);
    const proprioPortata = useRef(false);
    const chiaveAgende = agendeVisibili.map((a) => a.id).join(',');
    useEffect(() => {
        if (!proprioPortata.current && medicoCollegato && agendeLette && griglia) {
            proprioPortata.current = true;
            const sua = tutteAgende.find((a) => Number(a.medico_id) === Number(medicoCollegato));
            if (sua) daPortare.current = Number(sua.id);
        }
        if (daPortare.current == null) return;
        const indice = chiaveAgende.split(',').findIndex((id) => Number(id) === daPortare.current);
        if (indice < 0) return;
        daPortare.current = null;
        portaInVista(indice);
    }, [chiaveAgende, pagine.perPagina, agendeLette, griglia, tutteAgende, medicoCollegato]);

    /* La colonna dell'appuntamento aperto nel pannello resta a video: il
       pannello stringe la griglia (a 1440 da dieci a sei colonne) e senza
       questo l'appuntamento appena cliccato potrebbe finire in una pagina che
       non si vede. Solo quando cambia l'appuntamento, la larghezza o le
       agende: chi cambia pagina a mano col pannello aperto non viene
       riportato indietro. */
    const agendaDelPannello = pannello?.tipo === 'appuntamento'
        ? (griglia?.appuntamenti || []).find((x) => x.id === pannello.id)?.agenda_id ?? null
        : null;
    useEffect(() => {
        if (agendaDelPannello == null) return;
        const indice = chiaveAgende.split(',').findIndex((id) => Number(id) === Number(agendaDelPannello));
        if (indice >= 0) portaInVista(indice);
    }, [agendaDelPannello, pagine.perPagina, chiaveAgende]);

    const commutaAgenda = (id, mostrare) => {
        if (mostrare) {
            daPortare.current = Number(id);
            setNascoste((n) => n.filter((x) => x !== id));
            if (!idsInGriglia.includes(id)) setExtra((e) => [...e, id]);
        } else if (extra.includes(id)) {
            setExtra((e) => e.filter((x) => x !== id));
        } else {
            setNascoste((n) => [...n, id]);
        }
    };

    /* La proposta di partenza del pannello aperto senza un clic (Alex,
       4/10/2026): l'agenda del medico collegato all'utente; altrimenti
       l'agenda in vista - quella della settimana, o la prima colonna della
       griglia, a sinistra. Finche' gli elenchi non sono arrivati non si
       propone niente, e il pannello la prende quando arriva (se nessuno ha
       gia' scelto). E' solo una proposta: l'agenda si vede e si cambia nel
       pannello, e l'abilitazione la decide la rotta. */
    const agendaProposta = (() => {
        if (medicoCollegato) {
            if (!agendeLette) return null;
            const sua = tutteAgende.find((a) => Number(a.medico_id) === Number(medicoCollegato));
            if (sua) return Number(sua.id);
        }
        if (vista === 'settimana') return agendaSett ? Number(agendaSett) : null;
        return colonneInVista.length ? Number(colonneInVista[0].id) : null;
    })();

    const scelto = pannello?.tipo === 'appuntamento' ? pannello.id : null;
    const etichettaAgenda = (a) => (a.cognome ? `${a.cognome}, ${a.medico_nome}` : a.nome);

    return (
        <div className={`ag-wrap ${pannello ? 'ag-wrap--pannello' : ''}`} data-pannello-destro={pannello ? '' : undefined}>
            {/* ---------- testata ---------- */}
            <div className="ag-testata">
                <div className="ag-navigazione">
                    <button type="button" className="ag-freccia" onClick={() => cambiaGiorno(-passo)}
                            aria-label={vista === 'settimana' ? 'Settimana precedente' : 'Giorno precedente'}>
                        <ChevronLeft size={16} aria-hidden="true" />
                    </button>
                    <button type="button" onClick={() => setData(oggiIso())} className="ag-oggi">Oggi</button>
                    <button type="button" className="ag-freccia" onClick={() => cambiaGiorno(passo)}
                            aria-label={vista === 'settimana' ? 'Settimana successiva' : 'Giorno successivo'}>
                        <ChevronRight size={16} aria-hidden="true" />
                    </button>
                    {/* Non trattiene l'aggiornamento della pagina: il giorno
                        scritto qui finisce nell'indirizzo, e la pagina
                        ricaricata lo ritrova (utils/versioneInLinea.js). */}
                    <input type="date" value={data} onChange={(e) => setData(e.target.value)}
                           className="ag-data-campo" aria-label="Vai al giorno" data-non-trattiene />
                </div>
                <h1 className="ag-data">
                    {vista === 'settimana' ? titoloSettimana(lunediDi(giornoValido(data) || oggiIso())) : titoloGiorno(giornoValido(data) || oggiIso())}
                </h1>

                <div className="ag-viste" role="group" aria-label="Periodo">
                    <button type="button" className={vista === 'giorno' ? 'attiva' : ''}
                            aria-pressed={vista === 'giorno'} onClick={() => setVista('giorno')}>
                        Giorno
                    </button>
                    <button type="button" className={vista === 'settimana' ? 'attiva' : ''}
                            aria-pressed={vista === 'settimana'} onClick={() => setVista('settimana')}>
                        Settimana
                    </button>
                </div>

                <div className="ag-spazio" />

                {/* Le colonne della griglia: quali agende. Nella vista
                    settimanale si guarda un'agenda sola, e si sceglie qui. */}
                {vista === 'settimana' ? (
                    <select className="ag-scelta-settimana" value={agendaSett || ''} aria-label="Agenda della settimana"
                            data-non-trattiene
                            onChange={(e) => setAgendaSett(e.target.value ? parseInt(e.target.value, 10) : null)}>
                        <option value="">Scegli un&apos;agenda…</option>
                        {tutteAgende.map((a) => (
                            <option key={a.id} value={a.id}>{etichettaAgenda(a)}</option>
                        ))}
                    </select>
                ) : (
                    <>
                    <div className="ag-scelta-agende" ref={sceltaAgende}>
                        <button type="button" className="ag-agende" aria-expanded={agendeAperte}
                                aria-controls="ag-barra-agende" onClick={() => setAgendeAperte((v) => !v)}>
                            <Columns3 size={15} aria-hidden="true" />
                            <span className="ag-agende-colori" aria-hidden="true">
                                {agendeVisibili.slice(0, 5).map((a) => (
                                    <i key={a.id} style={coloreDi(a.id) ? { '--ag-colore': coloreDi(a.id) } : undefined} />
                                ))}
                            </span>
                            Agende
                            <span className="ag-agende-conto rm-numeri">{agendeVisibili.length}</span>
                        </button>
                        <BarraAgende
                            tutte={tutteAgende}
                            idsInGriglia={idsInGriglia}
                            nascoste={nascoste}
                            extra={extra}
                            onCommuta={commutaAgenda}
                            colore={coloreDi}
                            aperta={agendeAperte}
                            onChiudi={() => setAgendeAperte(false)}
                        />
                    </div>
                    {/* Le agende che non entrano a video stanno nelle pagine:
                        «agende 1-10 di 11», e le frecce per le altre. */}
                    <NavigazionePagine cosa="agende" pagine={pagine} totale={agendeVisibili.length} />
                    </>
                )}

                {/* Orari e stanze si raggiungevano SOLO dai riquadri in
                    Amministrazione. Ma gli orari di un'agenda si cercano
                    guardando l'agenda, e la stanza e' un campo
                    dell'appuntamento: e' qui che una persona si ferma e
                    pensa «adesso dovrei poterli cambiare».
                    Il riquadro in Amministrazione resta dov'era.

                    Solo a chi le rotte gia' ammettono: sono riservate ad
                    admin e segreteria, e un collegamento che porta a un
                    403 insegna a diffidare dei collegamenti. */}
                {organizza && (
                    <div className="ag-organizza">
                        <button type="button" onClick={() => navigate('/orari')}
                                title="Orari di apertura delle agende">
                            <Clock size={15} aria-hidden="true" /> Orari
                        </button>
                        <button type="button" onClick={() => navigate('/stanze')}
                                title="Stanze e apparecchiature">
                            <DoorOpen size={15} aria-hidden="true" /> Stanze
                        </button>
                    </div>
                )}

                <button type="button" className="ag-nuovo" onClick={() => apriNuovoLibero(true)}>
                    <Plus size={15} aria-hidden="true" /> Appuntamento
                </button>
            </div>

            {errore && <div className="ag-errore">{errore}</div>}

            <div className="ag-divisa">
                <div className="ag-vista">
                    {vista === 'settimana' ? (
                        <AgendaSettimana
                            agende={tutteAgende}
                            agendaId={agendaSett}
                            dal={lunediDi(giornoValido(data) || oggiIso())}
                            onApri={apriAppuntamento}
                            scelto={scelto}
                            versione={versione}
                        />
                    ) : caricamento && (!griglia || griglia.data !== data) ? (
                        /* Il giorno nuovo non e' ancora arrivato: niente
                           appuntamenti di un altro giorno sotto il titolo di
                           questo. Una rilettura dello stesso giorno invece
                           lascia la griglia dov'e' (e il pannello accanto). */
                        <div className="ag-stato">Caricamento…</div>
                    ) : !griglia?.agende.length ? (
                        <div className="ag-stato">
                            <Calendar size={28} aria-hidden="true" />
                            <p>Nessuno in sede {data === oggiIso() ? 'oggi' : 'questo giorno'}.</p>
                            <p className="ag-nota">
                                Aggiungi un&apos;agenda da «Agende» per vederla comunque.
                            </p>
                        </div>
                    ) : (
                        <div className="ag-griglia" style={{ '--ag-ora': `${ALTEZZA_ORA}px`, '--ag-colonne': colonneInVista.length }}>
                            {/* Un contenitore solo scorre, in verticale: le
                                intestazioni restano in cima e le ore a
                                sinistra (position: sticky). Di lato non
                                scorre mai: le colonne che non entrano a
                                104 px stanno nelle pagine dietro le frecce
                                (PagineDiColonne.jsx). */}
                            <div className="ag-corpo" ref={agganciaCorpo}>
                                <div className="ag-intestazioni">
                                    <div className="ag-angolo" />
                                    {colonneInVista.map((a) => {
                                        const c = coloreDi(a.id);
                                        const n = contoDi(a.id);
                                        return (
                                            <div key={a.id} className="ag-colonna-testa"
                                                 style={c ? { '--ag-colore': c } : undefined}>
                                                <b className="rm-tronca" title={a.nome}><i className="ag-colonna-colore" aria-hidden="true" />{a.nome}</b>
                                                <span className="ag-colonna-conto rm-numeri rm-tronca"
                                                      title={`${n} ${n === 1 ? 'appuntamento' : 'appuntamenti'}`}>
                                                    {n} {n === 1 ? 'appuntamento' : 'appuntamenti'}
                                                </span>
                                                {fascePer(a.id)[0]?.stanza && (
                                                    <em className="rm-tronca" title={fascePer(a.id)[0].stanza}>{fascePer(a.id)[0].stanza}</em>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>

                                <div className="ag-interno"
                                     style={{ height: (ORA_FINE - ORA_INIZIO + 1) * ALTEZZA_ORA }}>

                                    {/* le ore, e l'ora di adesso accanto alla linea */}
                                    <div className="ag-ore">
                                        {ORE.map((h) => (
                                            <div key={h} className={`ag-ora${oraCoperta(h, lineaAdesso) ? ' ag-ora--coperta' : ''}`}
                                                 style={{ height: ALTEZZA_ORA }}>
                                                <span className="rm-numeri">{dueCifre(h)}:00</span>
                                            </div>
                                        ))}
                                        {lineaAdesso != null && (
                                            <span className="ag-adesso-ora rm-numeri" style={{ top: lineaAdesso }}>
                                                {oraIta(adesso)}
                                            </span>
                                        )}
                                    </div>

                                    {/* colonne */}
                                    {colonneInVista.map((a) => {
                                        const c = coloreDi(a.id);
                                        return (
                                            <div key={a.id} className="ag-colonna" data-agenda={a.id}
                                                 ref={(el) => { colonne.current[a.id] = el; }}>
                                                {/* gli orari, un quarto d'ora ciascuno: un clic prenota */}
                                                {ORE.map((h) => [0, 15, 30, 45].map((m) => (
                                                    <div key={`${h}-${m}`}
                                                         className="ag-slot"
                                                         style={{ top: (h - ORA_INIZIO) * ALTEZZA_ORA + (m / 60) * ALTEZZA_ORA,
                                                                  height: ALTEZZA_ORA / 4 }}
                                                         onClick={() => nuovoDaGriglia(a, h, m)}
                                                    />
                                                )))}

                                                {/* fasce di lavoro: la tinta piu' chiara del colore
                                                    dell'agenda, sotto gli appuntamenti. Prima usava il
                                                    colore della stanza, che quasi sempre e' vuoto: da li'
                                                    il grigio ovunque. */}
                                                {fascePer(a.id).map((f, i) => (
                                                    <div key={i} className="ag-fascia"
                                                         style={{
                                                             top: pixelDiOrario(f.ora_inizio),
                                                             height: pixelDiOrario(f.ora_fine) - pixelDiOrario(f.ora_inizio),
                                                             ...(c ? { background: mescola(c, 0.93) } : {}),
                                                         }} />
                                                ))}

                                                {/* appuntamenti */}
                                                {appPer(a.id).map((x) => {
                                                    const largh = 100 / x.insieme;
                                                    const { top, height } = riquadroDi(x);
                                                    const sposto = trascina && trascina.id === x.id;
                                                    return (
                                                        <BloccoAppuntamento
                                                            key={x.id}
                                                            x={x}
                                                            colore={c}
                                                            scelto={scelto === x.id}
                                                            trascinato={!!sposto}
                                                            stile={{
                                                                top,
                                                                height,
                                                                left: `${x.colonna * largh}%`,
                                                                width: `calc(${largh}% - var(--rm-s1))`,
                                                                transform: sposto ? `translate(${trascina.dx}px, ${trascina.dy}px)` : undefined,
                                                            }}
                                                            onPointerDown={(e) => iniziaTrascinamento(e, x)}
                                                            onPointerMove={muoviTrascinamento}
                                                            onPointerUp={(e) => fineTrascinamento(e, x)}
                                                            onClick={(e) => clicSullAppuntamento(x, e)}
                                                        />
                                                    );
                                                })}
                                            </div>
                                        );
                                    })}

                                    {/* linea dell'ora corrente, sopra tutte le colonne */}
                                    {lineaAdesso != null && (
                                        <div className="ag-adesso" style={{ top: lineaAdesso }} />
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* ---------- il pannello a destra ---------- */}
                {pannello?.tipo === 'appuntamento' && (
                    <PannelloAppuntamento
                        key={pannello.id}
                        iniziale={pannello.iniziale}
                        fuoco={pannello.fuoco}
                        versione={versione}
                        colore={coloreDi(pannello.iniziale.agenda_id)}
                        nomeAgenda={tutteAgende.find((a) => a.id === pannello.iniziale.agenda_id)?.nome}
                        rifChiedi={chiediPannello}
                        onChiudi={() => {
                            /* Chiuso col fuoco dentro (da tastiera: Invio, poi
                               Esc), il fuoco torna sull'appuntamento da cui si
                               era partiti, invece di finire in cima alla pagina. */
                            const dentro = document.activeElement?.closest?.('.ag-pannello');
                            const id = pannello.id;
                            setPannello(null);
                            if (dentro) {
                                requestAnimationFrame(() => document.querySelector(`.ag-app[data-appuntamento="${id}"]`)?.focus());
                            }
                        }}
                        onAggiornato={() => { ricarica(); annunciaAgendaCambiata(); }}
                    />
                )}
                {pannello?.tipo === 'nuovo' && (
                    <PannelloNuovo
                        key={pannello.contesto.chiave || 0}
                        contesto={pannello.contesto}
                        agende={tutteAgende}
                        proposta={agendaProposta}
                        rifChiedi={chiediPannello}
                        onChiudi={() => setPannello(null)}
                        onCreato={(dove) => {
                            setPannello(null);
                            if (dove) mostraNuovo(dove); else ricarica();
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export default AgendaConsole;
